<template>
  <InsideContent :title="$t('APP.TRANSACTIONS.SUB-PURCHASES')">
    <Card>
      <div class="transactions-sub-purchases__actions">
        <TransactionsSubPurchasesSearch
          :errors="subscriptionFormErrors"
          @search="searchByTokens"
        />

        <div class="download-transactions-csv">
          <ElButton plain @click="openModal('downloadCsvModal')">
            {{ $t('APP.TRANSACTIONS.MONTHLY_CSV_EXPORT') }}
          </ElButton>

          <SendEmailSubPurchasesCsvModal v-model="downloadCsvModal" />
        </div>
      </div>
    </Card>

    <CardApiError
      v-if="hasError"
      :error-text="$t('APP.TRANSACTIONS.API_ERROR')"
      @retry="getPurchases"
    />

    <Card v-else-if="purchases.length" no-padding>
      <TransactionsSubPurchasesTable :purchases="purchases" />
    </Card>

    <Card v-else :is-loading="purchasesLoading">
      {{ $t('APP.TRANSACTIONS.NO_TRANSACTIONS') }}
    </Card>

    <TransactionsPagination
      :prev-page-url="prevPageUrl"
      :next-page-url="nextPageUrl"
      :is-loading="purchasesLoading"
      @change="setCurrentPage"
    />
  </InsideContent>
</template>

<script>
import { Apps } from '@/api/api';

import TransactionsPagination from '../TransactionsPagination';
import TransactionsSubPurchasesSearch from './TransactionsSubPurchasesSearch';
import TransactionsSubPurchasesTable from './TransactionsSubPurchasesTable';
import SendEmailSubPurchasesCsvModal from '../monthly-csv-export/SendEmailSubPurchasesCsvModal';
import Card from '@@/common/Card';
import InsideContent from '@@/common/layout/InsideContent';
import CardApiError from '@@/common/CardApiError';
import packageNameWatch from '@/mixins/apps/packageNameWatch';
import packageName from '@/mixins/packageName';
import lang from '@/mixins/lang';
import modal from '@/mixins/modal';
import permissions from '@/mixins/permissions';
import { mapGetters } from 'vuex';
import { APP_DETAILS } from '@/store/types';

export default {
  name: 'TransactionsSubPurchases',

  components: {
    TransactionsPagination,
    TransactionsSubPurchasesTable,
    TransactionsSubPurchasesSearch,
    Card,
    InsideContent,
    CardApiError,
    SendEmailSubPurchasesCsvModal,
  },

  mixins: [packageNameWatch, packageName, lang, permissions, modal],

  data() {
    return {
      purchases: [],
      purchasesLoading: true,
      prevPageUrl: null,
      nextPageUrl: null,
      filters: {
        page_no: 1,
        page_size: 20,
        renewal_token: null,
        subscription_token: null,
      },
      subscriptionFormErrors: null,
      downloadCsvModal: false,
    };
  },

  computed: {
    ...mapGetters({ app: APP_DETAILS }),
  },

  created() {
    if (!this.hasFinance(this.packageName)) {
      this.$router.replace({ name: 'apps-view' });
      return;
    }
    this.getInfo();
  },

  methods: {
    getInfo() {
      this.getPurchases();
    },

    getPurchases() {
      this.purchasesLoading = true;
      this.hasError = false;

      return Apps.getSubPurchases({
        packageName: this.packageName,
        ...this.filters,
      })
        .then(({ body: { purchases = [], prev, next } }) => {
          this.purchasesLoading = false;
          this.purchases = [...purchases];
          this.prevPageUrl = prev;
          this.nextPageUrl = next;
          this.subscriptionFormErrors = null;
        })
        .catch(({ body: { message, errors } = {} }) => {
          this.purchasesLoading = false;
          this.hasError = true;
          if (message) {
            this.$message.error(message);
          }
          if (errors) {
            this.subscriptionFormErrors = [...errors];
          }
        });
    },

    searchByTokens({ renewal_token, subscription_token }) {
      this.filters = {
        ...this.filters,
        page_no: 1,
        subscription_token,
        renewal_token,
      };
      this.getPurchases();
    },

    setCurrentPage(page) {
      this.filters = { ...this.filters, page_no: page };
      this.getPurchases();
    },
  },
};
</script>

<style lang="scss" scoped>
.transactions-sub-purchases {
  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    @include breakpoint(small only) {
      flex-direction: column !important;
    }
  }
}
</style>
